import { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { toast } from 'sonner';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@staff-app/ui';
import type { AgentDevice } from '@staff-app/shared-types';
import { STAFF_PROXY_PUSH_TO_SCREENS } from '@staff-app/graphql-client';
import { useDialogSubmitState } from '@/hooks/useDialogSubmitState';

interface Props {
  device: AgentDevice | null;
  onOpenChange: (open: boolean) => void;
}

interface PushResult {
  staffProxyPushToScreens: string;
}

/**
 * Assign a device's content by id and push it to the screen via open-api.
 * The backend auto-detects whether the id is an asset / playlist / schedule.
 */
export function SetAssignDialog({ device, onOpenChange }: Props) {
  const [contentId, setContentId] = useState('');

  // Prefill with the current assignment whenever a device is opened.
  useEffect(() => {
    setContentId(device?.currentAssignId ?? '');
  }, [device]);

  const [setMutation, { loading }] = useMutation<PushResult>(STAFF_PROXY_PUSH_TO_SCREENS, {
    refetchQueries: ['staffAgentDevices'],
  });

  const trimmed = contentId.trim();
  const { disabled: submitDisabled, disabledTooltip } = useDialogSubmitState({
    isSubmitting: loading,
    isRequiredFilled: !!trimmed,
    isDirty: trimmed !== (device?.currentAssignId ?? ''),
  });

  const handleSubmit = async () => {
    if (!device) return;
    try {
      await setMutation({ variables: { deviceId: device.deviceId, contentId: trimmed } });
      toast.success('Pushed to screen', {
        description: 'Content assigned and pushed to the device (type auto-detected).',
      });
      onOpenChange(false);
    } catch (err) {
      const msg = (err as { message?: string })?.message ?? 'Unknown error';
      // open-api rejects unknown content / cross-account / plan issues.
      toast.error('Push failed', { description: msg });
    }
  };

  return (
    <Dialog open={!!device} onOpenChange={onOpenChange}>
      <DialogContent data-testid="set-assign-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{'Update assignment'}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="assign-content-id">{'Content ID'}</Label>
            <Input
              id="assign-content-id"
              placeholder="asset / playlist / schedule id"
              value={contentId}
              onChange={(e) => setContentId(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !submitDisabled) handleSubmit();
              }}
              data-testid="set-assign-input"
              autoComplete="off"
              autoFocus
            />
            <p className="text-sm text-muted-foreground">
              {'Type is detected automatically and pushed to the device via open-api.'}
            </p>
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">{'Cancel'}</Button>
          </DialogClose>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={submitDisabled ? 0 : -1}>
                  <Button onClick={handleSubmit} disabled={submitDisabled} data-testid="set-assign-submit">
                    {loading ? 'Pushing…' : 'Push to screen'}
                  </Button>
                </span>
              </TooltipTrigger>
              {disabledTooltip ? <TooltipContent>{disabledTooltip}</TooltipContent> : null}
            </Tooltip>
          </TooltipProvider>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
